import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface ResultScreenProps {
  people: Record<string, number>;
  style: string | null;
  stage: string | null;
  taste: string | null;
  avoid: string | null;
  shopStyle: string | null;
  shopName: string | null;
  hasShop: boolean;
  schedule: string | null;
  onRetry: () => void;
}

const SHOPS = [
  {
    name: '수트룸 청담',
    area: '청담동',
    price: '100~130만원',
    tags: ['상담 만족도 높음', '핏 정평'],
    reviews: 218,
  },
  {
    name: '맥케이 예복',
    area: '강남',
    price: '80~110만원',
    tags: ['가성비', '촬영 구성 우수'],
    reviews: 156,
  },
  {
    name: '포레스트 예복',
    area: '종로',
    price: '70~95만원',
    tags: ['실속 추천', '원단 다양'],
    reviews: 134,
  },
];

const REVIEW_IMAGES = [
  'https://readdy.ai/api/search-image?query=elegant%20Korean%20wedding%20groom%20in%20premium%20navy%20tuxedo%2C%20soft%20natural%20lighting%2C%20editorial%20style%20portrait%2C%20minimal%20cream%20background%2C%20high%20fashion%20wedding%20photography&width=300&height=400&seq=yb-r1&orientation=portrait',
  'https://readdy.ai/api/search-image?query=bride%20and%20groom%20in%20Korean%20wedding%20attire%2C%20soft%20romantic%20atmosphere%2C%20editorial%20wedding%20photography%2C%20warm%20lighting%2C%20premium%20wedding%20magazine%20style&width=300&height=400&seq=yb-r2&orientation=portrait',
  'https://readdy.ai/api/search-image?query=close%20up%20of%20premium%20wedding%20suit%20fabric%20detail%2C%20soft%20gold%20button%2C%20Italian%20wool%20texture%2C%20editorial%20product%20photography%2C%20luxury%20menswear&width=300&height=300&seq=yb-r3&orientation=squarish',
  'https://readdy.ai/api/search-image?query=modern%20wedding%20suit%20showroom%20interior%2C%20minimal%20design%2C%20cream%20walls%2C%20soft%20ambient%20lighting%2C%20premium%20tailor%20shop%20atmosphere&width=300&height=300&seq=yb-r4&orientation=squarish',
];

export default function ResultScreen({
  people,
  style,
  stage,
  taste,
  avoid,
  shopStyle,
  shopName,
  hasShop,
  schedule,
  onRetry,
}: ResultScreenProps) {
  const navigate = useNavigate();

  const styleLabels: Record<string, string> = {
    reasonable: '합리적인 만족형',
    balance: '밸런스 중시형',
    premium: '프리미엄 집중형',
  };

  const stageLabels: Record<string, string> = {
    fixed: '예식 일정 확정 단계',
    adjusting: '날짜 조율 중',
    started: '준비 시작 단계',
  };

  const tasteLabels: Record<string, string> = {
    fit: '핏 중시',
    fabric: '원단 중시',
    consult: '상담 중시',
    photo: '사진 분위기 중시',
    price: '가격 만족도 중시',
  };

  const avoidLabels: Record<string, string> = {
    pressure: '부담없는 분위기',
    mismatch: '맞춤 추천',
    cost: '투명한 가격',
    wait: '간결한 과정',
    quality: '퀄리티 확실',
  };

  const shopStyleLabels: Record<string, string> = {
    expert: '전문가 추천 선호',
    compare: '직접 비교 선호',
    mixed: '혼합형 선호',
  };

  const totalPeople = Object.values(people).reduce((a, b) => a + b, 0);

  const tags = [
    styleLabels[style || ''],
    tasteLabels[taste || ''],
    avoidLabels[avoid || ''],
    shopStyleLabels[shopStyle || ''],
  ].filter(Boolean).slice(0, 4);

  return (
    <div className="min-h-[100dvh] bg-cream pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-cream-dark">
        <div className="flex items-center h-14 px-5">
          <button
            onClick={onRetry}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer active:scale-90 transition-transform"
            aria-label="뒤로가기"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <h1 className="ml-2 text-sm font-semibold text-navy">분석 결과</h1>
        </div>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="px-5 pt-6 pb-4"
      >
        <div className="bg-navy rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-28 h-28 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />

          <p className="text-xs text-softgold mb-3 font-medium tracking-wide">Atelier Essentials 분석 완료</p>
          <h2 className="font-serif text-2xl leading-relaxed mb-3">
            당신은
            <br />
            <span className="text-softgold">{styleLabels[style || '']} 예복 준비 타입</span>
            <br />
            입니다
          </h2>
          <p className="text-xs text-cream-dark leading-relaxed">
            8가지 기준을 바탕으로 예복 방향을 분석했습니다.
          </p>
        </div>
      </motion.div>

      {/* Tags */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="px-5 mb-6"
      >
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1.5 rounded-full bg-white border border-cream-dark text-xs font-medium text-navy"
            >
              {tag}
            </span>
          ))}
        </div>
      </motion.div>

      {/* Analysis cards */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="px-5 space-y-3 mb-6"
      >
        <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
              <i className="ri-wallet-3-line text-navy text-xs" />
            </div>
            <p className="text-sm font-semibold text-navy">준비 스타일</p>
          </div>
          <p className="text-xs text-navy-light pl-9 leading-relaxed">
            {styleLabels[style || ''] || '미선택'} — 
            {style === 'reasonable'
              ? ' 합리적인 예산 안에서 만족할 수 있는 옵션을 찾고 계세요. 종로 및 기타 지역 중심의 실속 예복점 추천.'
              : style === 'premium'
              ? ' 프리미엄 퀄리티와 브랜드 신뢰도를 중시하세요. 청담 중심의 상담 경험과 완성도 높은 업체 추천.'
              : ' 가격과 퀄리티의 균형을 중시하세요. 강남 중심의 다양한 옵션을 비교해보세요.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
              <i className="ri-calendar-line text-navy text-xs" />
            </div>
            <p className="text-sm font-semibold text-navy">현재 단계</p>
          </div>
          <p className="text-xs text-navy-light pl-9 leading-relaxed">
            {stageLabels[stage || ''] || '미선택'} — 
            {stage === 'fixed'
              ? ' 예복 맞춤 일정을 구체적으로 계획해야 하는 시점이에요. 3개월 전 예약을 추천드려요.'
              : stage === 'adjusting'
              ? ' 대략적인 시즌을 고려하여 예복을 미리 알아두는 단계예요.'
              : ' 천천히 기준을 잡아가며 다양한 업체를 둘러보세요.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
              <i className="ri-team-line text-navy text-xs" />
            </div>
            <p className="text-sm font-semibold text-navy">함께 준비하는 분</p>
          </div>
          <p className="text-xs text-navy-light pl-9 leading-relaxed">
            {totalPeople > 0
              ? `${totalPeople}명과 함께 준비하시네요. ${totalPeople >= 3 ? '단체 예약 시 상담 일정을 넉넉히 잡으세요.' : '소규모 방문으로 유연한 일정 조율이 가능해요.'}`
              : '아직 입력되지 않았어요.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center">
              <i className="ri-star-line text-navy text-xs" />
            </div>
            <p className="text-sm font-semibold text-navy">취향 분석</p>
          </div>
          <p className="text-xs text-navy-light pl-9 leading-relaxed">
            가장 중요한 기준은 <span className="font-semibold text-navy">{tasteLabels[taste || ''] || '미선택'}</span>이고,
            {' '}가장 피하고 싶은 것은 <span className="font-semibold text-navy">{avoidLabels[avoid || ''] || '미선택'}</span>이에요.
            {shopName ? ` ${shopName}에 관심이 있으시네요.` : ''}
          </p>
        </div>
      </motion.div>

      {/* Review based analysis */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="px-5 mb-6"
      >
        <h3 className="font-serif text-base text-navy mb-3">실제 후기 기반 분석</h3>
        <div className="bg-white rounded-2xl p-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-softgold/10 flex items-center justify-center shrink-0">
              <i className="ri-star-fill text-softgold text-xs" />
            </div>
            <div>
              <p className="text-xs font-semibold text-navy mb-1">후기에서 가장 많이 언급된 기준</p>
              <p className="text-[11px] text-navy-light leading-relaxed">
                {taste === 'fit'
                  ? '"핏이 다르다"는 후기가 가장 많았어요. 맞춤이나 반수제를 고려해보세요.'
                  : taste === 'fabric'
                  ? '"원단 퀄리티가 확실히 다르다"는 후기가 많아요. 원단 중심으로 비교해보세요.'
                  : taste === 'consult'
                  ? '"상담이 편했다"는 후기가 가장 많았어요. 상담 경험을 먼저 확인해보세요.'
                  : taste === 'photo'
                  ? '"사진이 정말 예쁘게 나왔다"는 후기가 많아요. 촬영 구성을 꼼꼼히 체크해보세요.'
                  : '"가격 대비 만족도가 높다"는 후기가 많아요. 구성을 꼼꼼히 비교해보세요.'}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-softgold/10 flex items-center justify-center shrink-0">
              <i className="ri-shield-check-line text-softgold text-xs" />
            </div>
            <div>
              <p className="text-xs font-semibold text-navy mb-1">피해야 할 포인트</p>
              <p className="text-[11px] text-navy-light leading-relaxed">
                {avoid === 'pressure'
                  ? '영업 압박이 강한 업체는 후기에서도 자주 지적돼요. 상담 전 리뷰를 꼭 확인하세요.'
                  : avoid === 'mismatch'
                  ? '추천 스타일과 실제 결과가 다른 경우가 있어요. 피팅 일정을 여유 있게 잡으세요.'
                  : avoid === 'cost'
                  ? '숨은 비용(수선, 악세서리 등)이 있는 경우가 많아요. 최종 견적을 미리 확인하세요.'
                  : avoid === 'wait'
                  ? '대기 시간이 긴 업체는 예약이 빡빡할 수 있어요. 평일 방문을 추천드려요.'
                  : '퀄리티 기대와 다른 경우가 있어요. 실제 후기 사진을 꼼꼼히 확인하세요.'}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Shop recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="px-5 mb-6"
      >
        <h3 className="font-serif text-base text-navy mb-3">이 기준에 맞는 인증 예복점</h3>
        <div className="space-y-3">
          {SHOPS.map((shopItem, idx) => (
            <motion.div
              key={shopItem.name}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 + idx * 0.08, duration: 0.35 }}
              className="bg-white rounded-2xl p-4"
            >
              <div className="flex gap-3">
                <div className="w-16 h-16 rounded-xl bg-cream-dark flex items-center justify-center shrink-0 overflow-hidden">
                  <img
                    src={`https://readdy.ai/api/search-image?query=elegant%20wedding%20tailor%20shop%20interior%20minimal%20cream%20navy%20atmosphere%20soft%20lighting%20premium%20bridal%20suit%20showroom&width=128&height=128&seq=${shopItem.name}&orientation=squarish`}
                    alt={shopItem.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-semibold text-navy">{shopItem.name}</p>
                    <span className="text-[10px] text-navy-light">{shopItem.area}</span>
                  </div>
                  <p className="text-xs text-softgold font-medium mb-1.5">{shopItem.price}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {shopItem.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-navy/5 text-navy-light"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] text-navy-light mt-1.5">후기 {shopItem.reviews}개</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Review photo feed */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className="px-5 mb-6"
      >
        <h3 className="font-serif text-base text-navy mb-3">실제 후기 사진</h3>
        <div className="grid grid-cols-2 gap-2">
          {REVIEW_IMAGES.map((src, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.65 + idx * 0.06, duration: 0.3 }}
              className={`rounded-xl overflow-hidden ${idx < 2 ? 'aspect-[3/4]' : 'aspect-square'}`}
            >
              <img
                src={src}
                alt={`후기 사진 ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Bottom CTA */}
      <div className="px-5 space-y-2">
        <button
          onClick={() => navigate('/quote')}
          className="w-full bg-navy hover:bg-navy-light text-white font-semibold rounded-xl py-4 text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
        >
          견적 요청하기
        </button>
        <button
          onClick={() => navigate('/vendors')}
          className="w-full bg-cream-light hover:bg-cream-dark text-navy font-medium rounded-xl py-3 text-sm transition-colors duration-200 border border-cream-dark whitespace-nowrap cursor-pointer"
        >
          예복 업체 전체 보기
        </button>
        <button
          onClick={onRetry}
          className="w-full text-navy-light text-xs py-2 cursor-pointer"
        >
          테스트 다시 하기
        </button>
      </div>
    </div>
  );
}